// Recipe state management hook
// Manages user recipes with backend sync
// Follows same pattern as useMealPlan for consistency

import { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3100';

/**
 * API helper for recipe operations
 */
async function fetchRecipes(token, effort = null) {
  const url = new URL(`${API_URL}/api/recipes`);
  if (effort) url.searchParams.set('effort', effort);
  
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  
  if (!response.ok) throw new Error('Failed to fetch recipes');
  return response.json();
}

async function createRecipe(recipe, token) {
  const response = await fetch(`${API_URL}/api/recipes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(recipe)
  });
  
  if (!response.ok) throw new Error('Failed to create recipe');
  return response.json();
}

async function updateRecipeApi(id, updates, token) {
  const response = await fetch(`${API_URL}/api/recipes/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) throw new Error('Failed to update recipe');
  return response.json();
}

async function deleteRecipeApi(id, token) {
  const response = await fetch(`${API_URL}/api/recipes/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` }
  });
  
  if (!response.ok) throw new Error('Failed to delete recipe');
  return response.json();
}

async function searchRecipesApi(query, token, effort = null) {
  const url = new URL(`${API_URL}/api/recipes/search`);
  url.searchParams.set('q', query);
  if (effort) url.searchParams.set('effort', effort);
  
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  
  if (!response.ok) throw new Error('Failed to search recipes');
  return response.json();
}

export default function useRecipes(getToken, isAuthenticated) {
  // ============ STATE ============

  const [recipes, setRecipes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Track if initial load is done
  const initialLoadDone = useRef(false);

  // ============ LOAD FROM BACKEND ============

  const loadRecipes = useCallback(async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const token = await getToken();
      const result = await fetchRecipes(token);
      
      if (result.success) {
        setRecipes(result.recipes || []);
      }
    } catch (err) {
      console.error('Failed to load recipes:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
      initialLoadDone.current = true;
    }
  }, [isAuthenticated, getToken]);

  // Load on mount
  useEffect(() => {
    if (!initialLoadDone.current) {
      loadRecipes();
    }
  }, [loadRecipes]);

  // ============ HANDLERS ============

  // Add a new recipe
  const addRecipe = useCallback(async (recipe) => {
    if (!isAuthenticated) return null;

    try {
      setIsSaving(true);
      setError(null);
      const token = await getToken();
      const result = await createRecipe(recipe, token);
      
      if (result.success) {
        setRecipes(prev => [...prev, result.recipe]);
        return result.recipe;
      }
      return null;
    } catch (err) {
      console.error('Failed to add recipe:', err);
      setError(err.message);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [isAuthenticated, getToken]);

  // Update an existing recipe
  const updateRecipe = useCallback(async (id, updates) => {
    if (!isAuthenticated) return null;

    try {
      setIsSaving(true);
      setError(null);
      const token = await getToken();
      const result = await updateRecipeApi(id, updates, token);
      
      if (result.success) {
        setRecipes(prev => prev.map(r => r.id === id ? result.recipe : r));
        return result.recipe;
      }
      return null;
    } catch (err) {
      console.error('Failed to update recipe:', err);
      setError(err.message);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [isAuthenticated, getToken]);

  // Delete a recipe
  const deleteRecipe = useCallback(async (id) => {
    if (!isAuthenticated) return false;

    try {
      setIsSaving(true);
      setError(null);
      const token = await getToken();
      const result = await deleteRecipeApi(id, token);
      
      if (result.success) {
        setRecipes(prev => prev.filter(r => r.id !== id));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to delete recipe:', err);
      setError(err.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [isAuthenticated, getToken]);

  // Search recipes
  const searchRecipes = useCallback(async (query, effort = null) => {
    if (!isAuthenticated) return [];

    try {
      const token = await getToken();
      const result = await searchRecipesApi(query, token, effort);
      
      if (result.success) {
        return result.recipes || [];
      }
      return [];
    } catch (err) {
      console.error('Failed to search recipes:', err);
      return [];
    }
  }, [isAuthenticated, getToken]);

  // Get recipe by ID
  const getRecipeById = useCallback((id) => {
    return recipes.find(r => r.id === id) || null;
  }, [recipes]);

  // Get recipe by name (fuzzy match)
  const getRecipeByName = useCallback((name) => {
    const lower = name.toLowerCase();
    return recipes.find(r => r.name.toLowerCase().includes(lower)) || null;
  }, [recipes]);

  // Filter by effort level
  const getRecipesByEffort = useCallback((effort) => {
    return recipes.filter(r => r.effort === effort);
  }, [recipes]);

  // ============ COMPUTED VALUES ============

  const recipeCount = recipes.length;
  
  const recipesByEffort = {
    'no-cook': recipes.filter(r => r.effort === 'no-cook'),
    'one-pan': recipes.filter(r => r.effort === 'one-pan'),
    'some-steps': recipes.filter(r => r.effort === 'some-steps'),
    'full-cook': recipes.filter(r => r.effort === 'full-cook')
  };

  // ============ RETURN ============

  return {
    // State
    recipes,
    isLoading,
    isSaving,
    error,

    // Computed
    recipeCount,
    recipesByEffort,

    // Handlers
    addRecipe,
    updateRecipe,
    deleteRecipe,
    searchRecipes,
    getRecipeById,
    getRecipeByName,
    getRecipesByEffort,
    loadRecipes
  };
}
